import { endOfDay, startOfDay, isValid } from 'date-fns';
import { ReportSettingState } from '..';
import {
  ClientCustomCalendarModel,
  ClientSetting,
  ReportType,
} from '../../../../models';
import { isValidDate } from '../../../../util';
import { PIT_MIN_DATE } from '../../../../util/constants';
import { ReportSettingPITState } from '../ReportSettingPITInput';
import { getReportEndDate } from './getReportEndDate';
import { validateReportSettingPITStateForReportType } from './validateReportSettingPITStateForReportType';
import { formatDateByLocale, isValidDateByFormat, parseLocaleSpecificDateStringToDefaultFormat } from '../../../../util/dateHelpers';
import { FeatureFlagContext } from '../../../../context/featureFlags';

export const validateReportSettingPITState = (
  pitState: ReportSettingPITState,
  reportType: ReportType,
  reportSettingState: ReportSettingState,
  clientSetting: ClientSetting,
  customCalendarSettings: ClientCustomCalendarModel,  
  dateFormat: string,
  localePreference: string,
  useFeatureFlags: FeatureFlagContext,
  reportScheduledDate?: Date,
): string[] => {
  if (!pitState.pitSwitchValue?.value) {
    return [];
  }
  if (!pitState.pitAsOfDate) {
    return [''];
  }

  const asOfDate = pitState.pitAsOfDate;
  const isValidAsOfDate = isValidDateByFormat(asOfDate, dateFormat);
  if (!isValidAsOfDate) {
    return [`Please enter a valid date (${dateFormat.toUpperCase()}).`];
  }

  const defaultFormatAsOfDateString = parseLocaleSpecificDateStringToDefaultFormat(asOfDate, localePreference);
  const defaultFormatAsOfDate = new Date(defaultFormatAsOfDateString);

  const validScheduleDate = isValidDate(reportScheduledDate);

  const runDate = validScheduleDate
    ? startOfDay(new Date(reportScheduledDate))
    : startOfDay(new Date());

  const reportEndDate = getReportEndDate(
    reportType,
    reportSettingState,
    customCalendarSettings,
  );

  const errs: string[] = [];
  //As of date <= today's date
  if (defaultFormatAsOfDate > runDate) {
    errs.push(
      validScheduleDate
        ? 'Please enter a date that falls on or before the Scheduled Run Date.'
        : 'Please enter a non-future date or instead schedule a run in the future.',
    );
  }
  // As of date must be >=1/1/2021
  let pitMinDate = PIT_MIN_DATE;
  if (isValid(clientSetting?.defaultAsOfDate)) {
    pitMinDate = clientSetting?.defaultAsOfDate;
  }

  if (defaultFormatAsOfDate < pitMinDate) {
    errs.push(
      `Currently our system cannot run Point in Time prior to ${formatDateByLocale(
        pitMinDate,
        localePreference,
      )}.`,
    );
  }

  //As of date must be >= report end date
  if (reportEndDate && endOfDay(defaultFormatAsOfDate) < endOfDay(reportEndDate)) {
    errs.push(
      `As of date cannot occur before Report End Date of ${formatDateByLocale(
        reportEndDate,
        localePreference,
      )}. Please adjust dates or disable Point in Time.`,
    );
  }

  // specify validations for this report type
  const reportErrors = validateReportSettingPITStateForReportType(
    {
      pitAsOfDate: defaultFormatAsOfDate,
      reportSettingState,
      customCalendarSettings,
      localePreference,
    },
    reportType,
  );

  return [...errs, ...reportErrors];
};
